// Copyright (c) 2021-22, iPivot, Corp.
'use strict'

import { createGlobalStyle } from 'styled-components'

export const GlobalStyles = createGlobalStyle`
    :root, [data-bs-theme=light] {
        --bs-font-sans-serif: ${props => props.theme.sansSerifFonts.join(',')} !important;
        --bs-font-monospace: ${props => props.theme.monospaceFonts.join(',')} !important;
        --bs-body-font-family: var(--bs-font-sans-serif) !important;

        --bs-primary: ${props => props.theme.colors.primary} !important;
        --bs-secondary: ${props => props.theme.colors.secondary} !important;
        --bs-danger: ${props => props.theme.colors.danger} !important;
        --bs-success: ${props => props.theme.colors.success} !important;
        --bs-warning: ${props => props.theme.colors.warning} !important;
        --bs-info: ${props => props.theme.colors.info} !important;

        --bs-secondary-color: ${props => props.theme.colors.secondaryColor} !important;

        --bs-body-bg: ${props => props.theme.colors.body.background} !important;
        --bs-body-color: ${props => props.theme.colors.body.color} !important;
    }

    body {
        font-size: 14px !important;
        font-synthesis: none;
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-text-size-adjust: 100%;

        .text-primary {
            color: ${props => props.theme.colors.text.primary.color} !important;
        }

        .text-secondary {
            color: ${props => props.theme.colors.text.secondary.color} !important;
        }

        .text-success {
            color: ${props => props.theme.colors.text.success.color} !important;
        }

        .text-danger {
            color: ${props => props.theme.colors.text.danger.color} !important;
        }

        .text-warning {
            color: ${props => props.theme.colors.text.warning.color} !important;
        }

        .text-info {
            color: ${props => props.theme.colors.text.info.color} !important;
        }

        .text-muted {
            color: ${props => props.theme.colors.text.muted.color} !important;
        }

        .valid-feedback {
            color: ${props => props.theme.colors.text.success.color};
        }

        .invalid-feedback {
            color: ${props => props.theme.colors.text.danger.color};
            font-size: 12px;
        }

        a {
            color: var(--bs-primary);
            text-decoration: none !important;

            &:hover {
                text-decoration: underline !important;
            }

            &:focus, &:focus-visible {
                outline: none;
            }
        }
    }

    h1 {
        font-size: 18px !important;
        font-weight: 600 !important;
    }

    h2 {
        font-size: 17px !important;
        font-weight: 500 !important;
    }

    h3 {
        font-size: 16px !important;
        font-weight: 500 !important;
    }

    h4 {
        font-size: 15px !important;
        font-weight: 500 !important;
    }

    h5 {
        font-size: 14px !important;
        font-weight: 500 !important;
    }

    strong {
        font-weight: 500 !important;
    }

    .vr {
        background-color: ${props => props.theme.colors.body.color} !important;
        width: 2px !important;
    }

    .action-icon {
        &:hover {
            cursor: pointer;
        }
    }

    .add-to-filter {
        color: ${props => props.theme.colors.a.color} !important;

        &:hover {
            cursor: zoom-in;
            text-decoration: underline;
        }
    }

    .add-to-filter-and-navigate {
        color: ${props => props.theme.colors.a.color} !important;

        &:hover {
            cursor: pointer;
            text-decoration: underline;
        }
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .cursor-not-allowed {
        cursor: not-allowed;
    }

    .avatar {
        background-color: ${props => props.theme.colors.avatar.backgroundColor} !important;
        color: ${props => props.theme.colors.avatar.color} !important;
        min-width: 32px;
        min-height: 32px;
        border-radius: 50%;
        border: 1px solid ${props => props.theme.colors.avatar.borderColor} !important;
        font-weight: 700;
        font-size: 12.8px;
        text-transform: uppercase;
        text-align: center;
        line-height: 30px;
    }

    //PDF
    @media print {
        * {
            -webkit-print-color-adjust: exact;
            -webkit-font-smoothing: antialiased;
        }
    }

    @page  {
        margin: 15mm;
        size: A4; /*or width then height 150mm 50mm*/
    }

    .pdf {
        font-family: Arial !important;
        font-size: 16pt !important;
        color: ${props => props.theme.colors.pdf.textColor} !important;

        h1 {
            font-size: 24pt !important;
            font-weight: 600 !important;
        }

        h2 {
            font-size: 22pt !important;
            font-weight: 600 !important;
        }

        h3 {
            font-size: 20pt !important;
            font-weight: 600 !important;
        }

        h4 {
            font-size: 18pt !important;
            font-weight: 600 !important;
        }

        h5 {
            font-size: 16pt !important;
            font-weight: 500 !important;
        }

        .cover {
            font-size: 18pt !important;

            h1 {
                font-size: 34pt !important;
                font-weight: 600 !important;
            }

            h2 {
                font-size: 30pt !important;
                font-weight: 600 !important;
            }

            h3 {
                font-size: 22pt !important;
                font-weight: 600 !important;
            }

            h4 {
                font-size: 22pt !important;
                font-weight: 500 !important;
            }

            h5 {
                font-size: 20pt !important;
                font-weight: 500 !important;
            }
        }

        .table {
            th {
                color: ${props => props.theme.colors.pdf.textColor};
            }

            td {
                color: ${props => props.theme.colors.pdf.textColor};
            }
        }
    }

    // Font-awesome Overrides
    .svg-inline--fa {
        font-size: 16px !important;
        vertical-align: middle;
    }

    // Recharts
    .recharts-responsive-container, .recharts-wrapper {
        &:focus-visible, div:focus-visible, svg:focus {
            outline: none;
        }
    }

    g&:focus,
    path&:focus {
        outline: none !important;
    }

    // react-datepicker
    .react-datepicker-wrapper {
        width: auto !important;
    }

    .react-datepicker {
        background-color: ${props => props.theme.colors.datePicker.backgroundColor} !important;
        border-color: ${props => props.theme.colors.datePicker.borderColor} !important;
        color: ${props => props.theme.colors.datePicker.color} !important;
        font-size: 14px !important;
        border-radius: 5px !important;
        width: 100% !important;

        .react-datepicker__header {
            background-color: ${props => props.theme.colors.datePicker.backgroundColor} !important;
            border-color: ${props => props.theme.colors.datePicker.borderColor} !important;
            color: ${props => props.theme.colors.datePicker.color} !important;

            .react-datepicker__current-month, .react-datepicker__day-name, .react-datepicker-time__header {
                color: ${props => props.theme.colors.datePicker.color} !important;
            }

            .react-datepicker-time__header, h2 {
                font-size: 14px !important;
                font-weight: 500 !important;
            }
        }

        .react-datepicker__month, .react-datepicker__month-container, .react-datepicker__time-container  {
            border-color: ${props => props.theme.colors.datePicker.borderColor} !important;
        }

        .react-datepicker__day, .react-datepicker__time-list-item  {
            color: ${props => props.theme.colors.datePicker.color} !important;

            &.react-datepicker__day--disabled, &.react-datepicker__time-list-item--disabled {
                color: ${props => props.theme.colors.datePicker.disabled.color} !important;

                &:hover {
                    background-color: transparent !important;
                    color: ${props => props.theme.colors.datePicker.disabled.color} !important;
                }
            }

            &:hover {
                background-color: ${props => props.theme.colors.datePicker.day.hover.backgroundColor} !important;
                color: ${props => props.theme.colors.datePicker.day.hover.color} !important;
            }
        }

        .react-datepicker__day--selected, .react-datepicker__day--in-range, .react-datepicker__time-list-item--selected {
            background-color: ${props => props.theme.colors.datePicker.selectedDay.backgroundColor} !important;
            color: ${props => props.theme.colors.datePicker.selectedDay.color} !important;
            font-weight: 400 !important;
        }

        .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range) {
            background-color: ${props => props.theme.colors.datePicker.inSelectingRange.backgroundColor} !important;
            color: ${props => props.theme.colors.datePicker.inSelectingRange.color} !important;

            &:hover {
                background-color: ${props => props.theme.colors.datePicker.day.hover.backgroundColor} !important;
                color: ${props => props.theme.colors.datePicker.day.hover.color} !important;
            }
        }

        .react-datepicker__navigation-icon::before {
            border-color: ${props => props.theme.colors.datePicker.navigationIcon.color} !important;
        }

        .react-datepicker__navigation:hover *::before {
            border-color: ${props => props.theme.colors.datePicker.navigationIcon.hover.color} !important;
        }
    }

    .react-datepicker__triangle {
        fill: ${props => props.theme.colors.datePicker.backgroundColor} !important;
        color: ${props => props.theme.colors.datePicker.backgroundColor} !important;
        stroke: ${props => props.theme.colors.datePicker.borderColor} !important;
    }

    // Bootstrap Overrides
    .accordion {
        background: ${props => props.theme.colors.accordion.background} !important;
        color: ${props => props.theme.colors.accordion.color} !important;
        margin-bottom: 20px;

        &:focus-visible {
            outline: none;
        }

        .accordion-header {
            border: none !important;
            font-size: 16px;
            padding: 0px;

            &:focus-visible {
                outline: none;
            }

            button {
                &:focus-visible {
                    outline: none;
                }
            }

            .accordion-button {
                background: ${props => props.theme.colors.accordion.background} !important;
                border: none !important;
                border-radius: 0px !important;
                border-bottom: 1px solid ${props => props.theme.colors.accordion.borderColor} !important;
                color: ${props => props.theme.colors.accordion.color} !important;
                padding: 4px 8px 3px 18px!important;

                &:not(.collapsed) {
                    background: ${props => props.theme.colors.accordion.header.open.background};
                    border: none !important;
                    border-bottom: 1px solid ${props => props.theme.colors.accordion.borderColor} !important;
                    color: ${props => props.theme.colors.accordion.header.open.color} !important;
                    box-shadow: none;
                }

                &.collapsed {
                    box-shadow: none;

                    &:focus-visible {
                        outline: none;
                    }
                }

                &:focus {
                    box-shadow: none;
                }

                &:hover {
                    background: ${props => props.theme.colors.accordion.header.open.background};
                    color: ${props => props.theme.colors.accordion.header.open.color} !important;
                }

                &::before {
                    width: 16px;
                    height: 9px;
                    position: absolute;
                    left: 0px;
                    bottom: 0px;
                    content: '';
                    display: inline-block;
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='9' viewBox='0 0 16 9' fill='${props =>
                        props.theme.colors.accordion.header
                            .beforeColor}'%3e%3cpath fill-rule='evenodd' d='M4.56934 9H0V6.24316L4.56934 9ZM15.1338 9H10.1367L0.143555 3V0L15.1338 9Z'/%3e%3c/svg%3e");
                }

                &::after {
                    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='${props => props.theme.colors.accordion.header.afterColor}'%3e%3cpath d='M9.39695 12.8132C9.20495 12.8127 9.01315 12.7392 8.86756 12.5918L4.62928 8.33007C4.33709 8.03627 4.33839 7.56127 4.6322 7.26908C4.92601 6.97688 5.401 6.97819 5.6932 7.272L9.40294 11.0022L13.1332 7.29249C13.427 7.00029 13.902 7.0016 14.1942 7.29541C14.4864 7.58921 14.485 8.06421 14.1912 8.3564L9.92955 12.5947C9.78315 12.7403 9.59095 12.8138 9.39895 12.8132L9.39695 12.8132Z'/%3e%3c/svg%3e");
                }

                &:hover {
                    &::after {
                        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='${props => props.theme.colors.accordion.header.hover.afterColor}'%3e%3cpath d='M9.39695 12.8132C9.20495 12.8127 9.01315 12.7392 8.86756 12.5918L4.62928 8.33007C4.33709 8.03627 4.33839 7.56127 4.6322 7.26908C4.92601 6.97688 5.401 6.97819 5.6932 7.272L9.40294 11.0022L13.1332 7.29249C13.427 7.00029 13.902 7.0016 14.1942 7.29541C14.4864 7.58921 14.485 8.06421 14.1912 8.3564L9.92955 12.5947C9.78315 12.7403 9.59095 12.8138 9.39895 12.8132L9.39695 12.8132Z'/%3e%3c/svg%3e");
                    }
                }
            }
        }

        .accordion-body {
            padding: 10px 0px;
        }

        .accordion-item {
            background: ${props => props.theme.colors.accordion.background} !important;
            border: none;

            &:focus-visible {
                outline: none;
            }
        }

        .accordion-collapse {
            background: ${props => props.theme.colors.accordion.background} !important;
            color: ${props => props.theme.colors.accordion.color} !important;

            &:focus-visible {
                outline: none;
            }
        }
    }

    .alert {
        font-weight: 500;

        &.alert-danger {
            background: ${props => props.theme.colors.alert.danger.background};
            border: ${props => props.theme.colors.alert.danger.border};
            color: ${props => props.theme.colors.alert.danger.color};
        }

        &.alert-primary {
            background: ${props => props.theme.colors.alert.primary.background};
            border: ${props => props.theme.colors.alert.primary.border};
            color: ${props => props.theme.colors.alert.primary.color};
        }

        &.alert-secondary {
            background: ${props => props.theme.colors.alert.secondary.background};
            border: ${props => props.theme.colors.alert.secondary.border};
            color: ${props => props.theme.colors.alert.secondary.color};
        }

        &.alert-success {
            background: ${props => props.theme.colors.alert.success.background};
            border: ${props => props.theme.colors.alert.success.border};
            color: ${props => props.theme.colors.alert.success.color};
        }

        &.alert-warning {
            background: ${props => props.theme.colors.alert.warning.background};
            border: ${props => props.theme.colors.alert.warning.border};
            color: ${props => props.theme.colors.alert.warning.color};
        }

        &.alert-info {
            background: ${props => props.theme.colors.alert.info.background};
            border: ${props => props.theme.colors.alert.info.border};
            color: ${props => props.theme.colors.alert.info.color};
        }
    }

    .badge {
        border-width: 1px !important;
        border-style: solid !important;

        &.bg-danger {
            background: ${props => props.theme.colors.badge.danger.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.danger.borderColor} !important;
            color: ${props => props.theme.colors.badge.danger.color} !important;
        }

        &.bg-primary {
            background: ${props => props.theme.colors.badge.primary.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.primary.borderColor} !important;
            color: ${props => props.theme.colors.badge.primary.color} !important;
        }

        &.bg-secondary {
            background: ${props => props.theme.colors.badge.secondary.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.secondary.borderColor} !important;
            color: ${props => props.theme.colors.badge.secondary.color} !important;
        }

        &.bg-success {
            background: ${props => props.theme.colors.badge.success.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.success.borderColor} !important;
            color: ${props => props.theme.colors.badge.success.color} !important;
        }

        &.bg-warning {
            background: ${props => props.theme.colors.badge.warning.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.warning.borderColor} !important;
            color: ${props => props.theme.colors.badge.warning.color} !important;
        }

        &.bg-info {
            background: ${props => props.theme.colors.badge.info.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.info.borderColor} !important;
            color: ${props => props.theme.colors.badge.info.color} !important;
        }

        &.bg-critical {
            background-color: ${props => props.theme.colors.badge.critical.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.critical.borderColor} !important;
            color: ${props => props.theme.colors.badge.critical.color} !important;
        }

        &.bg-high {
            background-color: ${props => props.theme.colors.badge.high.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.high.borderColor} !important;
            color: ${props => props.theme.colors.badge.high.color} !important;
        }

        &.bg-medium {
            background-color: ${props => props.theme.colors.badge.medium.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.medium.borderColor} !important;
            color: ${props => props.theme.colors.badge.medium.color} !important;
        }

        &.bg-low {
            background-color: ${props => props.theme.colors.badge.low.backgroundColor} !important;
            border-color: ${props => props.theme.colors.badge.low.borderColor} !important;
            color: ${props => props.theme.colors.badge.low.color} !important;
        }
    }

    .breadcrumb-item {
        font-weight: 500;

        +.breadcrumb-item::before {
            font-size: 16px !important;
            content: '>' !important;
            line-height: 20px !important;
        }

        a {
            color: ${props => props.theme.colors.breadcrumbs.color} !important;

            &:hover {
                color: ${props => props.theme.colors.breadcrumbs.hover.color} !important;
            }
        }

        &.active {
            color: ${props => props.theme.colors.breadcrumbs.active.color} !important;
        }
    }

    .dropdown-menu {
        font-size: 14px !important;
        background-color: ${props => props.theme.colors.dropdownMenu.backgroundColor} !important;
        border-color: ${props => props.theme.colors.dropdownMenu.borderColor} !important;
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, 0.5rem 0.5rem 0.875rem 0rem #5f61ff4d !important;
        padding: 6px !important;

        &.show {
            margin-top: 0px !important;
        }

        .dropdown-item {
            color: ${props => props.theme.colors.dropdownMenu.item.color} !important;
            margin-bottom: 2px !important;
            border-style: solid !important;
            border-width: 1px !important;
            border-color: transparent !important;
            padding: 10px 12px !important;
            line-height: 14px;

            &.disabled {
                color: ${props => props.theme.colors.dropdownMenu.item.disabled.color} !important;
            }

            &:hover {
                background-color: ${props => props.theme.colors.dropdownMenu.item.hover.backgroundColor} !important;
                border-radius: 12px !important;
                border-style: solid !important;
                border-width: 1px !important;
                border-color: ${props => props.theme.colors.dropdownMenu.item.hover.borderColor} !important;
                color: ${props => props.theme.colors.dropdownMenu.item.hover.color} !important;
                text-decoration: none !important;
            }
        }
    }

    .list-group-item {
        background: ${props => props.theme.colors.listGroupItem.background} !important;
        border-color: ${props => props.theme.colors.listGroupItem.borderColor} !important;
        color: ${props => props.theme.colors.listGroupItem.color} !important;
    }

    .nav-pills {
        .nav-link {
            color: ${props => props.theme.colors.a.color} !important;

            &.active {
                background: ${props => props.theme.colors.navPills.background} !important;
                border-bottom: 2px solid ${props => props.theme.colors.a.color};
                border-radius: 0px;
                color: ${props => props.theme.colors.a.color} !important;
                font-weight: 600;
            }
        }
    }

    .nav {
        background-color: ${props => props.theme.colors.nav.backgroundColor};
        line-height: 18px !important;
        color: ${props => props.theme.colors.nav.color};

        .nav-link {
            color: ${props => props.theme.colors.nav.color};

            &.nav-link:focus,
            &.nav-link:hover {
                color: ${props => props.theme.colors.nav.hover.color};
            }

            &.disabled {
                color: ${props => props.theme.colors.nav.disabled.color};
            }
        }

        .navbar-toggler {
            color: ${props => props.theme.colors.nav.color} !important;
        }

        hr {
            border-top: 1px solid ${props => props.theme.colors.nav.separator.borderColor} !important;
        }
    }

    .progress-bar {
        background-color: ${props => props.theme.colors.progressBar.backgroundColor} !important;
        color: ${props => props.theme.colors.progressBar.color};
    }

    .card {
        --bs-card-border-radius: ${props => props.theme.colors.card.borderRadius} !important;

        background: ${props => props.theme.colors.card.background} !important;
        border-color: ${props => props.theme.colors.card.borderColor} !important;
        border-width: 1px !important;
        border-style: solid !important;
        color: ${props => props.theme.colors.card.color};
        margin-bottom: 1rem;
        padding: 0rem 1rem;

        .card-header {
            background: ${props => props.theme.colors.card.header.background};
            border-bottom-color: ${props => props.theme.colors.card.header.borderBottomColor};
            border-bottom-width: 1px !important;
            border-bottom-style: solid !important;
            color: ${props => props.theme.colors.card.header.color};
            padding: 1.5rem 0.75rem;

            .card-title {
                font-size: 15px !important;
                font-weight: 500 !important;
                margin-bottom: 0rem;
            }
        }

        .card-body {
            padding: 1.25rem 0.75rem;
        }

        .card-footer {
            background: ${props => props.theme.colors.card.footer.background};
            border-top-color: ${props => props.theme.colors.card.footer.borderTopColor};
            border-top-width: 1px !important;
            border-top-style: solid !important;
            color: ${props => props.theme.colors.card.footer.color};
            padding: 1rem 0.75rem;
        }

        &.highlight-card {
            background: ${props => props.theme.colors.card.highlight.background} !important;
            border: transparent !important;
            border-width: 1px !important;
            border-style: solid !important;
        }

        &.input-list-card {
            background-color: ${props => props.theme.colors.card.inputList.backgroundColor} !important;
            border-color: ${props => props.theme.colors.card.inputList.borderColor} !important;
            border-width: 1px !important;
            border-style: solid !important;
        }
    }

    .dropdown-toggle::after {
        display: none !important;
    }

    .flex-wrap {
        row-gap: 10px;
    }

    .pagination {
        --bs-pagination-color: ${props => props.theme.colors.pagination.color} !important;
        --bs-pagination-bg: ${props => props.theme.colors.pagination.backgroundColor} !important;
        --bs-pagination-border-color: ${props => props.theme.colors.pagination.borderColor} !important;
        --bs-pagination-hover-color: ${props => props.theme.colors.pagination.hover.color} !important;
        --bs-pagination-hover-bg: ${props => props.theme.colors.pagination.hover.backgroundColor} !important;
        --bs-pagination-hover-border-color: ${props => props.theme.colors.pagination.hover.borderColor} !important;
        --bs-pagination-active-color: ${props => props.theme.colors.pagination.active.color} !important;
        --bs-pagination-active-bg: ${props => props.theme.colors.pagination.active.backgroundColor} !important;
        --bs-pagination-active-border-color: ${props => props.theme.colors.pagination.active.borderColor} !important;
        --bs-pagination-disabled-bg: ${props => props.theme.colors.pagination.disabled.backgroundColor} !important;
        --bs-pagination-disabled-border-color: ${props => props.theme.colors.pagination.disabled.borderColor} !important;

        .page-item {
            margin: 3px !important;

            .page-link {
                border-radius: 4px !important;
                font-size: 12px;
                font-weight: 600;
                padding: 5px 7px !important;
                text-decoration: none !important;
            }

            &.ellipsis {
                --bs-pagination-disabled-bg: transparent !important;
                --bs-pagination-disabled-border-color: transparent !important;
            }
        }

    }

    .table {
        --bs-table-bg: ${props => props.theme.colors.table.backgroundColor} !important;

        border-color: transparent !important;
        border-bottom-color: ${props => props.theme.colors.table.borderColor} !important;
        border-collapse: collapse;
        border-width: 1px;
        vertical-align: middle !important;

        thead {
            th {
                --bs-table-bg: ${props => props.theme.colors.table.header.backgroundColor} !important;
                --bs-border-width: 0px !important;

                color: ${props => props.theme.colors.table.header.color} !important;
                font-weight: 600 !important;
                padding: 12px 8px !important;
            }
        }

        tbody tr {
            td {
                color: ${props => props.theme.colors.table.color} !important;
            }

            &:hover {
                --bs-table-hover-bg: ${props => props.theme.colors.table.hover.backgroundColor} !important;
                color: ${props => props.theme.colors.table.hover.color} !important;
            }
        }
    }

    .table-responsive .dropdown {
        position: static;
    }

    .table-striped>tbody>tr:nth-of-type(odd)>* {
        color: ${props => props.theme.colors.table.color} !important;
    }

    .table-responsive::-webkit-scrollbar {
        -webkit-appearance: none;
    }

    .table-responsive::-webkit-scrollbar:vertical {
        width: 12px;
    }

    .table-responsive::-webkit-scrollbar:horizontal {
        height: 12px;
    }

    .table-responsive::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, .5);
        border-radius: 10px;
        border: 2px solid #ffffff;
    }

    .table-responsive::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #ffffff;
    }

    .btn {
        --bs-btn-disabled-opacity: 0.4 !important;
        min-height: 38px;
        font-size: 14px !important;
        --bs-btn-border-radius: 4px !important;

        &.btn-sm {
            min-height: 32px !important;
        }

        &:hover {
            opacity: .95;
        }

        &:active {
            outline: 0;
        }

        &.btn-light {
            color: ${props => props.theme.colors.button.light.color} !important;
        }

        &.btn-primary {
            --bs-btn-bg: ${props => props.theme.colors.button.primary.backgroundColor} !important;
            --bs-btn-border-color: ${props => props.theme.colors.button.primary.borderColor} !important;
            --bs-btn-color: ${props => props.theme.colors.button.primary.color} !important;

            --bs-btn-disabled-bg: ${props => props.theme.colors.button.primary.disabled.backgroundColor} !important;
            --bs-btn-disabled-border-color: ${props => props.theme.colors.button.primary.disabled.borderColor} !important;
            --bs-btn-disabled-color: ${props => props.theme.colors.button.primary.disabled.color} !important;

            --bs-btn-hover-bg: ${props => props.theme.colors.button.primary.hover.backgroundColor} !important;
            --bs-btn-hover-border-color: ${props => props.theme.colors.button.primary.hover.borderColor} !important;
            --bs-btn-hover-color: ${props => props.theme.colors.button.primary.hover.color} !important;

            --bs-btn-active-bg: ${props => props.theme.colors.button.primary.active.backgroundColor} !important;
            --bs-btn-active-border-color: ${props => props.theme.colors.button.primary.active.borderColor} !important;
            --bs-btn-active-color: ${props => props.theme.colors.button.primary.active.color} !important;
        }

        &.btn-secondary {
            --bs-btn-bg: ${props => props.theme.colors.button.secondary.backgroundColor} !important;
            --bs-btn-border-color: ${props => props.theme.colors.button.secondary.borderColor} !important;
            --bs-btn-color: ${props => props.theme.colors.button.secondary.color} !important;

            --bs-btn-disabled-bg: ${props => props.theme.colors.button.secondary.backgroundColor} !important;
            --bs-btn-disabled-border-color: ${props => props.theme.colors.button.secondary.borderColor} !important;
            --bs-btn-disabled-color: ${props => props.theme.colors.button.secondary.disabled.color} !important;

            --bs-btn-hover-bg: ${props => props.theme.colors.button.secondary.hover.backgroundColor} !important;
            --bs-btn-hover-border-color: ${props => props.theme.colors.button.secondary.hover.borderColor} !important;
            --bs-btn-hover-color: ${props => props.theme.colors.button.secondary.hover.color} !important;

            --bs-btn-active-bg: ${props => props.theme.colors.button.secondary.active.backgroundColor} !important;
            --bs-btn-active-border-color: ${props => props.theme.colors.button.secondary.active.borderColor} !important;
            --bs-btn-active-color: ${props => props.theme.colors.button.secondary.active.color} !important;
        }

        &.btn-outline-secondary {
            --bs-btn-bg: ${props => props.theme.colors.button.outlineSecondary.backgroundColor} !important;
            --bs-btn-border-color: ${props => props.theme.colors.button.outlineSecondary.borderColor} !important;
            --bs-btn-color: ${props => props.theme.colors.button.outlineSecondary.color} !important;

            --bs-btn-disabled-bg: ${props => props.theme.colors.button.outlineSecondary.backgroundColor} !important;
            --bs-btn-disabled-color: ${props => props.theme.colors.button.outlineSecondary.disabled.color} !important;
            --bs-btn-disabled-border-color: ${props => props.theme.colors.button.outlineSecondary.borderColor} !important;

            --bs-btn-hover-bg: ${props => props.theme.colors.button.outlineSecondary.hover.backgroundColor} !important;
            --bs-btn-hover-border-color: ${props => props.theme.colors.button.outlineSecondary.hover.borderColor} !important;
            --bs-btn-hover-color: ${props => props.theme.colors.button.outlineSecondary.hover.color} !important;

            --bs-btn-active-bg: ${props => props.theme.colors.button.outlineSecondary.active.backgroundColor} !important;
            --bs-btn-active-border-color: ${props => props.theme.colors.button.outlineSecondary.active.borderColor} !important;
            --bs-btn-active-color: ${props => props.theme.colors.button.outlineSecondary.active.color} !important;
        }

        &.btn-danger {
            --bs-btn-bg: ${props => props.theme.colors.button.danger.backgroundColor} !important;
            --bs-btn-border-color: ${props => props.theme.colors.button.danger.borderColor} !important;
            --bs-btn-color: ${props => props.theme.colors.button.danger.color} !important;

            --bs-btn-disabled-bg: ${props => props.theme.colors.button.danger.backgroundColor} !important;
            --bs-btn-disabled-color: ${props => props.theme.colors.button.danger.disabled.color} !important;
            --bs-btn-disabled-border-color: ${props => props.theme.colors.button.danger.borderColor} !important;

            --bs-btn-hover-bg: ${props => props.theme.colors.button.danger.hover.backgroundColor} !important;
            --bs-btn-hover-border-color: ${props => props.theme.colors.button.danger.hover.borderColor} !important;
            --bs-btn-hover-color: ${props => props.theme.colors.button.danger.hover.color} !important;

            --bs-btn-active-bg: ${props => props.theme.colors.button.danger.active.backgroundColor} !important;
            --bs-btn-active-border-color: ${props => props.theme.colors.button.danger.active.borderColor} !important;
            --bs-btn-active-color: ${props => props.theme.colors.button.danger.active.color} !important;
        }

        &.btn-link {
            text-decoration: none !important;
            font-weight: 600 !important;
            font-size: 12px !important;
            line-height: 14px !important;

            --bs-btn-bg: ${props => props.theme.colors.button.link.backgroundColor} !important;
            --bs-btn-border-color: ${props => props.theme.colors.button.link.borderColor} !important;
            --bs-btn-color: ${props => props.theme.colors.button.link.color} !important;

            --bs-btn-disabled-bg: ${props => props.theme.colors.button.link.backgroundColor} !important;
            --bs-btn-disabled-color: ${props => props.theme.colors.button.link.disabled.color} !important;
            --bs-btn-disabled-border-color: ${props => props.theme.colors.button.link.borderColor} !important;

            --bs-btn-hover-bg: ${props => props.theme.colors.button.link.hover.backgroundColor} !important;
            --bs-btn-hover-border-color: ${props => props.theme.colors.button.link.hover.borderColor} !important;
            --bs-btn-hover-color: ${props => props.theme.colors.button.link.hover.color} !important;

            --bs-btn-active-bg: ${props => props.theme.colors.button.link.active.backgroundColor} !important;
            --bs-btn-active-border-color: ${props => props.theme.colors.button.link.active.borderColor} !important;
            --bs-btn-active-color: ${props => props.theme.colors.button.link.active.color} !important;
        }
    }

    .btn-group>.btn-group:not(:last-child)>.btn, .btn-group>.btn:not(:last-child):not(.dropdown-toggle) {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
    }

    .dropdown-item.active,
    .dropdown-item:active {
        background: ${props => props.theme.colors.dropdown.button.secondary.active.background} !important;
        color: ${props => props.theme.colors.dropdown.button.secondary.active.color} !important;
    }

    .form-check-input[type=checkbox] {
        height: 18px !important;
        width: 18px !important;
        margin-top: 0px;
        vertical-align: middle;

        background-color: ${props => props.theme.colors.formCheck.backgroundColor} !important;
        border-color: ${props => props.theme.colors.formCheck.borderColor} !important;

        &:indeterminate {
            background-color: ${props => props.theme.colors.formCheck.checked.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formCheck.checked.borderColor} !important;

            &:hover {
                background-color: ${props => props.theme.colors.formCheck.checked.hover.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formCheck.checked.hover.borderColor} !important;
            }

            &:focus:not(:hover) {
                background-color: ${props => props.theme.colors.formCheck.checked.focus.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formCheck.checked.focus.borderColor} !important;
            }
        }

        &:hover {
            background-color: ${props => props.theme.colors.formCheck.hover.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formCheck.hover.borderColor} !important;
        }

        &:focus:not(:hover) {
            background-color: ${props => props.theme.colors.formCheck.focus.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formCheck.focus.borderColor} !important;
        }

        &:checked {
            background-color: ${props => props.theme.colors.formCheck.checked.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formCheck.checked.borderColor} !important;

            &:hover {
                background-color: ${props => props.theme.colors.formCheck.checked.hover.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formCheck.checked.hover.borderColor} !important;
            }

            &:focus:not(:hover) {
                background-color: ${props => props.theme.colors.formCheck.checked.focus.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formCheck.checked.focus.borderColor} !important;
            }
        }

        &:focus {
            box-shadow: none !important;
        }

        &.is-invalid {
            ~.form-check-label {
                color: ${props => props.theme.colors.formCheck.isInvalid.color} !important;
            }
        }
    }

    .form-check-input[type=radio] {
        height: 18px !important;
        width: 18px !important;
        margin-top: 0px;
        vertical-align: middle;

        background-color: ${props => props.theme.colors.formRadio.backgroundColor} !important;
        border-color: ${props => props.theme.colors.formRadio.borderColor} !important;

        &:hover {
            background-color: ${props => props.theme.colors.formRadio.hover.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formRadio.hover.borderColor} !important;
        }

        &:focus:not(:hover) {
            background-color: ${props => props.theme.colors.formRadio.focus.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formRadio.focus.borderColor} !important;
        }

        &:checked {
            background-color: ${props => props.theme.colors.formRadio.checked.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formRadio.checked.borderColor} !important;

            &:hover {
                background-color: ${props => props.theme.colors.formRadio.checked.hover.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formRadio.checked.hover.borderColor} !important;
            }

            &:focus:not(:hover) {
                background-color: ${props => props.theme.colors.formRadio.checked.focus.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formRadio.checked.focus.borderColor} !important;
            }
        }

        &:focus {
            box-shadow: none !important;
        }

        &.is-invalid {
            ~.form-check-label {
                color: ${props => props.theme.colors.formRadio.isInvalid.color} !important;
            }
        }
    }

    .form-check-label {
        padding-left: 5px;
    }

    .form-switch  {
        .form-check-input {
            height: 22px !important;
            width: 32px !important;
            background-color: ${props => props.theme.colors.formSwitch.backgroundColor} !important;
            background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.knobColor}'/></svg>") !important;
            border-color: ${props => props.theme.colors.formSwitch.borderColor} !important;

            &:hover {
                background-color: ${props => props.theme.colors.formSwitch.hover.backgroundColor} !important;
                background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.hover.knobColor}'/></svg>") !important;
                border-color: ${props => props.theme.colors.formSwitch.hover.borderColor} !important;
            }

            &:focus:not(:hover) {
                background-color: ${props => props.theme.colors.formSwitch.focus.backgroundColor} !important;
                background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.focus.knobColor}'/></svg>") !important;
                border-color: ${props => props.theme.colors.formSwitch.focus.borderColor} !important;
            }

            &:checked {
                background-color: ${props => props.theme.colors.formSwitch.checked.backgroundColor} !important;
                background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.checked.knobColor}'/></svg>") !important;
                border-color: ${props => props.theme.colors.formSwitch.checked.borderColor} !important;

                &:hover {
                    background-color: ${props => props.theme.colors.formSwitch.checked.hover.backgroundColor} !important;
                    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.checked.hover.knobColor}'/></svg>") !important;
                    border-color: ${props => props.theme.colors.formSwitch.checked.hover.borderColor} !important;
                }

                &:focus:not(:hover) {
                    background-color: ${props => props.theme.colors.formSwitch.checked.focus.backgroundColor} !important;
                    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.checked.focus.knobColor}'/></svg>") !important;
                    border-color: ${props => props.theme.colors.formSwitch.checked.focus.borderColor} !important;
                }
            }

            &:focus {
                box-shadow: none !important;
            }

            &.is-invalid {
                background-color: ${props => props.theme.colors.formSwitch.isInvalid.backgroundColor} !important;
                background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.isInvalid.knobColor}'/></svg>") !important;
                border-color: ${props => props.theme.colors.formSwitch.isInvalid.borderColor} !important;

                &:hover {
                    background-color: ${props => props.theme.colors.formSwitch.isInvalid.hover.backgroundColor} !important;
                    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='${props => props.theme.colors.formSwitch.isInvalid.hover.knobColor}'/></svg>") !important;
                    border-color: ${props => props.theme.colors.formSwitch.isInvalid.hover.borderColor} !important;
                }

                ~.form-check-label {
                    color: ${props => props.theme.colors.formSwitch.isInvalid.color} !important;
                }
            }
        }

        .form-check-label {
            padding-top: 2px;
        }
    }

    textarea {
        height: auto !important;
    }

    .form-control {
        background-color: ${props => props.theme.colors.formControl.backgroundColor} !important;
        color: ${props => props.theme.colors.formControl.color} !important;
        border-color: ${props => props.theme.colors.formControl.borderColor} !important;
        border-radius: 4px !important;
        font-size: 14px !important;
        height: 38px; !important;

        &:disabled {
            background-color: ${props => props.theme.colors.formControl.disabled.backgroundColor} !important;
        }

        &:hover {
            background-color: ${props => props.theme.colors.formControl.hover.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formControl.hover.borderColor} !important;
            box-shadow: ${props => props.theme.colors.formControl.hover.boxShadow} !important;
            color: ${props => props.theme.colors.formControl.hover.color} !important;
        }

        &:focus {
            background-color: ${props => props.theme.colors.formControl.focus.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formControl.focus.borderColor} !important;
            box-shadow: ${props => props.theme.colors.formControl.focus.boxShadow} !important;
            color: ${props => props.theme.colors.formControl.focus.color} !important;
        }

        &.is-valid {
            background-color: ${props => props.theme.colors.formControl.isValid.backgroundColor} !important;
            border-color: ${props => props.theme.colors.formControl.isValid.borderColor} !important;
            box-shadow: ${props => props.theme.colors.formControl.isValid.boxShadow} !important;
            color: ${props => props.theme.colors.formControl.isValid.color} !important;
        }

        &.is-invalid {
            background-color: ${props => props.theme.colors.formControl.isInvalid.backgroundColor} !important;
            background-image: none !important;
            padding-right: 12px !important;
            border-color: ${props => props.theme.colors.formControl.isInvalid.borderColor} !important;
            box-shadow: ${props => props.theme.colors.formControl.isInvalid.boxShadow} !important;
            color: ${props => props.theme.colors.formControl.isInvalid.color} !important;
        }
    }

    .form-label {
        color: ${props => props.theme.colors.formLabel.color} !important;
        font-weight: 600;
    }

    .form-select {
        background-color: ${props => props.theme.colors.formControl.backgroundColor} !important;
        color: ${props => props.theme.colors.formControl.color} !important;
        border-color: ${props => props.theme.colors.formControl.borderColor} !important;
        border-radius: 4px !important;
        font-size: 14px !important;
        height: 38px; !important;

        &:disabled {
            background-color: ${props => props.theme.colors.formControl.disabled.backgroundColor} !important;
        }
    }

    hr {
        border-color: ${props => props.theme.colors.hr.borderColor} !important;
        opacity: 1 !important;
    }

    .input-group>:not(:first-child):not(:last-child) .form-control,
    .input-group>:not(:first-child):not(:last-child).form-control {
        border-radius: 0px !important;
    }

    .input-group>:first-child:not(:last-child) .form-control,
    .input-group>:first-child:not(:last-child).form-control {
        border-top-right-radius: 0px !important;
        border-bottom-right-radius: 0px !important;
    }

    .input-group>:last-child:not(:first-child) .form-control,
    .input-group>:last-child:not(:first-child).form-control {
        border-top-left-radius: 0px !important;
        border-bottom-left-radius: 0px !important;
    }

    .input-group-text {
        background: ${props => props.theme.colors.inputGroup.background} !important;
        color: ${props => props.theme.colors.inputGroup.color} !important;
        border: ${props => props.theme.colors.inputGroup.border} !important;
    }

    .modal {
        color: ${props => props.theme.colors.modal.color};

        .modal-content {
            box-shadow: ${props => props.theme.colors.modal.boxShadow};
            border: none;
        }

        .modal-header {
            background-color: ${props => props.theme.colors.modal.backgroundColor};
            border: none;
            padding-bottom: 0px;

            .modal-title {
                font-size: 16px;
                font-weight: 600;
            }
        }

        .modal-body {
            background-color: ${props => props.theme.colors.modal.backgroundColor};
            border: none;
        }

        .modal-footer {
            background-color: ${props => props.theme.colors.modal.backgroundColor};
            border: none;
            padding-top: 0px;
        }
    }

    .nav-tabs {
        background: ${props => props.theme.colors.navTabs.backgroundColor} !important;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        border-bottom-color: ${props => props.theme.colors.navTabs.borderColor} !important;
        border-right: none !important;

        .nav-link {
            background: ${props => props.theme.colors.navTabs.navLink.backgroundColor} !important;
            border: none !important;
            color: ${props => props.theme.colors.navTabs.navLink.color} !important;
            font-weight: 600 !important;

            &.active {
                color: ${props => props.theme.colors.navTabs.navLink.active.color} !important;
                border-bottom-color: ${props => props.theme.colors.navTabs.navLink.active.borderColor} !important;
                border-bottom-width: 2px !important;
                border-bottom-style: solid !important;
            }

            &:hover {
                color: ${props => props.theme.colors.navTabs.navLink.hover.color} !important;
            }
        }

    }

    .tab-content {
        .tab-pane {
            padding-top: 16px !important;
        }
    }

    .popover {
        --bs-popover-bg: ${props => props.theme.colors.popover.backgroundColor} !important;
        --bs-popover-header-bg: ${props => props.theme.colors.popover.backgroundColor} !important;
        --bs-popover-border-color: ${props => props.theme.colors.popover.borderColor} !important;
        border-radius: 5px !important;
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, 0.5rem 0.5rem 0.875rem 0rem #5f61ff4d !important;
        white-space: nowrap !important;
        min-width: fit-content !important;
        padding: 6px !important;

        .popover-arrow {
            display: none !important;
        }

        .popover-body {
            padding: 6px !important;

            .nav-link {
                padding: 10px 12px !important;
                margin-bottom: 2px !important;
                border-style: solid !important;
                border-width: 1px !important;
                border-color: transparent !important;
                padding: 10px 12px !important;
                line-height: 14px !important;

                &.active {
                    background-color: ${props => props.theme.colors.popover.navItem.active.backgroundColor} !important;
                    border-color: ${props => props.theme.colors.popover.navItem.active.borderColor} !important;
                    border-radius: 12px !important;
                    color: ${props => props.theme.colors.popover.navItem.active.color} !important;
                }

                &.disabled {
                    color: ${props => props.theme.colors.popover.navItem.disabled.color};
                }

                &:hover {
                    background-color: ${props => props.theme.colors.dropdownMenu.item.hover.backgroundColor} !important;
                    border-radius: 12px !important;
                    border-style: solid !important;
                    border-width: 1px !important;
                    border-color: ${props => props.theme.colors.dropdownMenu.item.hover.borderColor} !important;
                    text-decoration: none !important;
                }
            }

            hr {
                margin: 8px 0px !important;
            }
        }
    }

    // D3 Tip
    .d3-tip {
        background: ${props => props.theme.colors.charts.tooltip.backgroundColor};
        border: ${props => props.theme.colors.charts.tooltip.border};
        border-radius: 5px;
        box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, 0.5rem 0.5rem 0.875rem 0rem #5f61ff4d !important;
        color: ${props => props.theme.colors.charts.tooltip.textColor};
        padding: 15px;
    }

    .swagger-ui {
        font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
        color: ${props => props.theme.colors.body.color} !important;
        padding-top: 10px !important;

        .information-container {
            padding: 0px !important;
        }

        .wrapper {
            padding: 0px !important;
            max-width: none !important;
        }

        .scheme-container {
            background-color: transparent !important;
            box-shadow: none !important;
            border-top: 1px solid ${props => props.theme.colors.hr.borderColor} !important;
            border-bottom: 1px solid ${props => props.theme.colors.hr.borderColor} !important;

            .wrapper {
                align-items: center !important;
            }

            .servers-title {
                color: ${props => props.theme.colors.formLabel.color} !important;
                font-weight: 600;
            }

            .servers {
                padding-top: 10px !important;
            }
        }

        .info {
            margin-top: 0px !important;

            .title, .description, p {
                font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
                color: ${props => props.theme.colors.body.color} !important;

                small {
                    top: 0px !important;
                    vertical-align: top !important;
                    background-color: ${props => props.theme.colors.badge.primary.backgroundColor} !important;

                    &.version-stamp {
                        background-color: ${props => props.theme.colors.badge.success.backgroundColor} !important;
                    }
                }
            }

            a {
                font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
                color: ${props => props.theme.colors.a.color} !important;
            }
        }

        .btn {
            border-width: 1px !important;
            border-style: solid !important;
            border-radius: 4px !important;
            box-shadow: none !important;
            color: ${props => props.theme.colors.body.color} !important;
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            font-size: 14px !important;
            height: 100%;
            min-height: 38px;
            padding: 6px 12px !important;

            &:hover {
                box-shadow: none !important;
            }

            &.authorize, &.try-out__btn, &.btn-done {
                background-color: ${props => props.theme.colors.button.outlineSecondary.backgroundColor} !important;
                border-color: ${props => props.theme.colors.button.outlineSecondary.borderColor} !important;
                color: ${props => props.theme.colors.button.outlineSecondary.color} !important;
                align-items: center !important;

                span {
                    padding: 0px 5px 0px 0px !important;
                }

                svg {
                    fill: ${props => props.theme.colors.button.outlineSecondary.color} !important;
                }

                &:hover {
                    background-color: ${props => props.theme.colors.button.outlineSecondary.hover.backgroundColor} !important;
                    border-color: ${props => props.theme.colors.button.outlineSecondary.hover.borderColor} !important;
                    color: ${props => props.theme.colors.button.outlineSecondary.hover.color} !important;

                    svg {
                        fill: ${props => props.theme.colors.button.outlineSecondary.hover.color} !important;
                    }
                }
            }

            &.execute {
                background-color: ${props => props.theme.colors.button.primary.backgroundColor} !important;
                border-color: ${props => props.theme.colors.button.primary.borderColor} !important;
                color: ${props => props.theme.colors.button.primary.color} !important;
                align-items: center !important;

                span {
                    padding: 0px 5px 0px 0px !important;
                }

                svg {
                    fill: ${props => props.theme.colors.button.primary.color} !important;
                }

                &:hover {
                    background-color: ${props => props.theme.colors.button.primary.hover.backgroundColor} !important;
                    border-color: ${props => props.theme.colors.button.primary.hover.borderColor} !important;
                    color: ${props => props.theme.colors.button.primary.hover.color} !important;

                    svg {
                        fill: ${props => props.theme.colors.button.primary.hover.color} !important;
                    }
                }
            }
        }

        .auth-btn-wrapper {
            button:first-child {
                margin-right: 10px !important;
            }
        }

        code {
            font-family: ${props => props.theme.monospaceFonts.join(',')} !important;
        }

        .tab li {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        .response-col_links, .response-col_status {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        .responses-inner {
            h4 {
                font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
                color: ${props => props.theme.colors.body.color} !important;
            }
        }

        .model {
            font-family: ${props => props.theme.monospaceFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        .model-title {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        table thead tr td {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        .parameter__name, .parameter__type {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;

            span, &.required:after {
                color: ${props => props.theme.colors.text.danger.color} !important;
            }
        }

        .parameter__in {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.text.muted.color} !important;
        }

        .opblock-tag {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;
        }

        .opblock {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            color: ${props => props.theme.colors.body.color} !important;

            .opblock-section-header h4 {
                font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
                color: ${props => props.theme.colors.body.color} !important;
            }

            .opblock-summary-method {
                font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
                color: ${props => props.theme.colors.body.color} !important;
            }

            .opblock-summary-path, .opblock-summary-path__deprecated {
                font-family: ${props => props.theme.monospaceFonts.join(',')} !important;
                color: ${props => props.theme.colors.body.color} !important;
            }
        }

        .opblock-summary {
            .opblock-summary-control {
                outline: none !important;
            }

            &.opblock-summary-get {
                .opblock-summary-method {
                    background-color: ${props => props.theme.colors.badge.primary.backgroundColor} !important;
                    color: ${props => props.theme.colors.badge.primary.color} !important;
                }
            }

            &.opblock-summary-post {
                .opblock-summary-method {
                    background-color: ${props => props.theme.colors.badge.success.backgroundColor} !important;
                    color: ${props => props.theme.colors.badge.success.color} !important;
                }
            }

            &.opblock-summary-put {
                .opblock-summary-method {
                    background-color: ${props => props.theme.colors.badge.warning.backgroundColor} !important;
                    color: ${props => props.theme.colors.badge.warning.color} !important;
                }
            }

            &.opblock-summary-delete {
                .opblock-summary-method {
                    background-color: ${props => props.theme.colors.badge.danger.backgroundColor} !important;
                    color: ${props => props.theme.colors.badge.danger.color} !important;
                }
            }
        }

        .opblock.opblock-get {
            border-color: ${props => props.theme.colors.badge.primary.backgroundColor} !important;
            background-color: ${props => props.theme.colors.badge.primary.backgroundColor}14 !important;

            .opblock-summary {
                border-color: ${props => props.theme.colors.badge.primary.backgroundColor} !important;
            }

            .tab-header .tab-item.active h4 span:after {
                background-color: ${props => props.theme.colors.badge.primary.backgroundColor} !important;
            }
        }

        .opblock.opblock-post {
            border-color: ${props => props.theme.colors.badge.success.backgroundColor} !important;
            background-color: ${props => props.theme.colors.badge.success.backgroundColor}14 !important;

            .opblock-summary {
                border-color: ${props => props.theme.colors.badge.success.backgroundColor} !important;
            }

            .tab-header .tab-item.active h4 span:after {
                background-color: ${props => props.theme.colors.badge.success.backgroundColor} !important;
            }
        }

        .opblock.opblock-put {
            border-color: ${props => props.theme.colors.badge.warning.backgroundColor} !important;
            background-color: ${props => props.theme.colors.badge.warning.backgroundColor}14 !important;

            .opblock-summary {
                border-color: ${props => props.theme.colors.badge.warning.backgroundColor} !important;
            }

            .tab-header .tab-item.active h4 span:after {
                background-color: ${props => props.theme.colors.badge.warning.backgroundColor} !important;
            }
        }

        .opblock.opblock-delete {
            border-color: ${props => props.theme.colors.badge.danger.backgroundColor} !important;
            background-color: ${props => props.theme.colors.badge.danger.backgroundColor}14 !important;

            .opblock-summary {
                border-color: ${props => props.theme.colors.badge.danger.backgroundColor} !important;
            }

            .tab-header .tab-item.active h4 span:after {
                background-color: ${props => props.theme.colors.badge.danger.backgroundColor} !important;
            }
        }

        .opblock-body .opblock-title_normal {
            font-family: ${props => props.theme.sansSerifFonts.join(',')} !important;
            padding-bottom: 0px !important;
            margin-bottom: 0px !important;
        }

        .opblock-body pre.microlight {
            font-family: ${props => props.theme.monospaceFonts.join(',')} !important;
        }

        input, select, textarea {
            background-color: ${props => props.theme.colors.formControl.backgroundColor} !important;
            color: ${props => props.theme.colors.formControl.color} !important;
            border-color: ${props => props.theme.colors.formControl.borderColor} !important;
            box-shadow: none !important;
            border-radius: 4px !important;
            font-size: 14px !important;
            height: 38px; !important;
            font-weight: 400 !important;

            &:focus, &:focus-visible {
                background-color: ${props => props.theme.colors.formControl.focus.backgroundColor} !important;
                border-color: ${props => props.theme.colors.formControl.focus.borderColor} !important;
                box-shadow: none !important;
                color: ${props => props.theme.colors.formControl.focus.color} !important;
                outline: none !important;
            }
        }

        select[multiple] {
            height: 180px;
            resize: vertical;
        }
    }
`
